/**
 * Stay AI bundle grouping for Rebuy Smart Cart.
 *
 * Bundle line items share a `_bundle_id` line-item property; the parent line additionally
 * carries `_bundle_parent`. From the customer's perspective the bundle is one unit:
 *
 *   - Child lines are hidden via the `.bundle-child` class (CSS in theme.css.liquid).
 *   - The parent line shows the bundle total (sum of all sibling line prices).
 *   - Qty +/- on the parent scales every sibling proportionally; removing it removes all.
 *
 * Loaded as an ES module from layout/theme.liquid. Self-contained: this file only depends
 * on `window.Rebuy` (smart cart) being available, which it announces via `rebuy:cart.ready`.
 */

const RETAG_DEBOUNCE_MS = 50

class BundleCartHandler {
  constructor(Rebuy) {
    this._Rebuy = Rebuy
  }

  // ---------- Helpers ----------

  isBundleItem(item) {
    return !!item?.properties?._bundle_id
  }

  isBundleParent(item) {
    const flag = item?.properties?._bundle_parent
    return flag === 'true' || flag === true
  }

  findBundleSiblings(item) {
    const bundleId = item?.properties?._bundle_id
    if (!bundleId) return []
    return this._Rebuy.Cart.items().filter(
      (i) => i.key !== item.key && i.properties?._bundle_id === bundleId
    )
  }

  formatMoney(cents) {
    const locale = document.documentElement.lang || 'en-US'
    const currency = window.Shopify?.currency?.active || 'USD'
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100)
    } catch (e) {
      return '$' + (cents / 100).toFixed(2)
    }
  }

  removeWholeBundle(item) {
    const bundleId = item?.properties?._bundle_id
    if (!bundleId) return
    this._Rebuy.Cart.items()
      .filter((i) => i.properties?._bundle_id === bundleId)
      .forEach((i) => this._Rebuy.Cart.removeItem(i))
  }

  handleBundleIncrease(item) {
    if (this.isBundleParent(item)) {
      // Parent +: snap back to 1. Customers who want a second bundle re-add from the builder.
      this._Rebuy.Cart.changeItem({ id: item.key, quantity: 1 })
      return
    }
    // Child + (only reachable by unhiding the child via dev tools) — bundle is compromised.
    this.removeWholeBundle(item)
  }

  handleBundleDecrease(item) {
    if (this.isBundleParent(item)) {
      const newQty = item.quantity - 1
      if (newQty <= 0) {
        this.removeWholeBundle(item)
      } else {
        // Parent –: still >0, snap back to 1 (qty>1 would be invalid).
        this._Rebuy.Cart.changeItem({ id: item.key, quantity: 1 })
      }
      return
    }
    // Child – (dev-tools tamper) — bundle is compromised.
    this.removeWholeBundle(item)
  }

  handleBundleRemove(item) {
    this.findBundleSiblings(item).forEach((s) => this._Rebuy.Cart.removeItem(s))
  }

  bindCascade() {
    document.addEventListener('rebuy:smartcart.line-item-increase', ({ detail: { item } }) => {
      if (this.isBundleItem(item)) this.handleBundleIncrease(item)
    })
    document.addEventListener('rebuy:smartcart.line-item-decrease', ({ detail: { item } }) => {
      if (this.isBundleItem(item)) this.handleBundleDecrease(item)
    })
    document.addEventListener('rebuy:smartcart.line-item-removed', ({ detail: { item } }) => {
      if (this.isBundleItem(item)) this.handleBundleRemove(item)
    })
  }

  paintBundles() {
    const items = this._Rebuy.Cart?.items?.() || []
    const root = document.getElementById('rebuy-cart')
    if (!root) return

    root.querySelectorAll('.bundle-line').forEach((el) => {
      el.classList.remove('bundle-line', 'bundle-parent', 'bundle-child')
    })
    root
      .querySelectorAll('.bundle-original-price, .bundle-total-price')
      .forEach((el) => el.remove())

    const bundles = new Map()
    items.forEach((item) => {
      const id = item.properties?._bundle_id
      if (!id) return
      if (!bundles.has(id)) bundles.set(id, { total: 0, original: 0, parentNode: null })
      const b = bundles.get(id)
      const linePrice =
        typeof item.line_price === 'number' ? item.line_price : (item.price || 0) * item.quantity
      const origLine =
        typeof item.original_line_price === 'number' ? item.original_line_price : linePrice
      b.total += linePrice
      b.original += origLine
    })
    if (!bundles.size) return

    const flyoutItems = Array.from(root.querySelectorAll('.rebuy-cart__flyout-item'))
    const keyAttrs = [
      'data-key',
      'data-line-item-key',
      'data-rebuy-cart-item-key',
      'data-cart-item-key',
    ]

    items.forEach((item, index) => {
      if (!this.isBundleItem(item)) return

      let node = null
      for (const attr of keyAttrs) {
        node = root.querySelector(`[${attr}="${item.key}"]`)
        if (node) break
      }
      if (!node) {
        node = flyoutItems.find((el) =>
          Object.values(el.dataset || {}).some((v) => v === item.key)
        )
      }
      if (!node && flyoutItems[index]) node = flyoutItems[index]
      if (!node) return

      const line = node.closest('.rebuy-cart__flyout-item') || node
      line.classList.add('bundle-line')
      line.classList.toggle('bundle-parent', this.isBundleParent(item))
      line.classList.toggle('bundle-child', !this.isBundleParent(item))

      if (this.isBundleParent(item)) {
        const b = bundles.get(item.properties._bundle_id)
        if (b) b.parentNode = line

        line.querySelectorAll('a[href]').forEach((a) => {
          a.removeAttribute('href')
        })

        this.injectEditButton(line, item)
      }
    })

    bundles.forEach((b) => {
      if (!b.parentNode) return
      this.applyBundlePrice(b.parentNode, b.total, b.original)
    })
  }

  // Replace the parent line's price with the bundle total (and an optional strikethrough original)
  applyBundlePrice(parentLine, totalCents, originalCents) {
    const priceSelectors = [
      '.rebuy-cart__flyout-item-price',
      '.rebuy-cart__flyout-item-subtotal',
      '.rebuy-cart__flyout-item-price-list',
      '.rebuy-cart__flyout-item-info-price',
    ]
    let priceEl = null
    for (const sel of priceSelectors) {
      priceEl = parentLine.querySelector(sel)
      if (priceEl) break
    }
    if (!priceEl) {
      priceEl = Array.from(parentLine.querySelectorAll('*')).find((el) =>
        /[$€£¥]/.test(el.textContent || '')
      )
    }
    if (!priceEl) return

    const total = this.formatMoney(totalCents)
    if (originalCents > totalCents) {
      const orig = this.formatMoney(originalCents)
      priceEl.innerHTML =
        `<span class="bundle-original-price" style="text-decoration:line-through;opacity:.6;margin-right:6px">${orig}</span>` +
        `<span class="bundle-total-price">${total}</span>`
    } else {
      priceEl.innerHTML = `<span class="bundle-total-price">${total}</span>`
    }
  }

  injectEditButton(parentLine, item) {
    const collection = item.properties?._bundle_collection
    const data = item.properties?._bundle_data
    if (!collection || !data) return

    let btn = parentLine.querySelector('.bundle-edit-btn')
    if (!btn) {
      btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'bundle-edit-btn rebuy-cart__flyout-item-remove'
      btn.textContent = 'Edit'
      const info = parentLine.querySelector('.rebuy-cart__flyout-item-info .rebuy-cart__flyout-item-quantity') || parentLine
      info.prepend(btn)
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        this.editBundle(btn.dataset.collection, btn.dataset.bundleData)
      })
    }
    // Refresh the payload every repaint so the button always points at the current line data.
    btn.dataset.collection = collection
    btn.dataset.bundleData = data
  }

  editBundle(collection, data) {
    try {
      const products = JSON.parse(data);
      const stored = JSON.parse(localStorage.getItem('bundleProducts') || '{}');
      stored[collection] = products;
      localStorage.setItem('bundleProducts', JSON.stringify(stored));
    } catch (e) {
      console.error('Bundle edit: could not restore bundle products', e);
    }
    const root = window.Shopify?.routes?.root || '/';
    window.location.href = root + 'collections/' + collection + '?view=bundle-builder';
  }

  bindRepaint() {
    let scheduled = null
    let cartObserver = null
    let cartObserverTarget = null

    const reattach = () => {
      if (cartObserver && cartObserverTarget) {
        cartObserver.observe(cartObserverTarget, { childList: true, subtree: true })
      }
    }

    const repaint = () => {
      if (scheduled) return
      scheduled = setTimeout(() => {
        scheduled = null
        if (cartObserver) cartObserver.disconnect()
        try {
          this.paintBundles()
        } finally {
          reattach()
        }
      }, RETAG_DEBOUNCE_MS)
    }

    document.addEventListener('rebuy:cart.change', repaint)
    document.addEventListener('rebuy:smartcart.show', repaint)
    document.addEventListener('rebuy:smartcart.refresh', repaint)
    document.addEventListener('rebuy:cart.ready', repaint)

    const attach = (target) => {
      cartObserverTarget = target
      cartObserver = new MutationObserver(repaint)
      reattach()
    }
    const cartRoot = document.getElementById('rebuy-cart')
    if (cartRoot) {
      attach(cartRoot)
    } else {
      const bodyObserver = new MutationObserver(() => {
        const t = document.getElementById('rebuy-cart')
        if (!t) return
        attach(t)
        bodyObserver.disconnect()
      })
      bodyObserver.observe(document.body, { childList: true, subtree: true })
    }

    repaint()
  }

  init() {
    if (!this._Rebuy) return
    this.bindCascade()
    this.bindRepaint()
  }
}

document.addEventListener('rebuy:cart.ready', () => {
  if (!window.Rebuy) return
  new BundleCartHandler(window.Rebuy).init()
})
